/**
 * Auto-save the active subtask's teacher model + prompt while the user is
 * still editing, so closing the page or the modal without clicking
 * Next/Done never loses the latest keystroke.
 *
 * Shared between:
 *   - ``TaskPromptSetupModal`` (header "Task Prompt" modal)
 *   - ``pages/ModelSetup`` (full page reached via "Model and Prompt Setup")
 *
 * Both surfaces edit the same backend fields via ``/save_teacher_and_prompt``
 * and both need to honour the composite-task propagation rule (first-time
 * sessions fan out to empty sibling subtasks; later edits don't), so we
 * keep that logic in one place.
 *
 * Behaviour mirrors the model-notes auto-save in ``ModelDetailsDrawer``:
 *   - Debounced ``AUTOSAVE_DEBOUNCE_MS`` (0.7s) after the last edit.
 *   - In-flight saves cancel on dep change / unmount.
 *   - Empty prompt deletions are saved too. The page/modal still validate
 *     required prompts before continuing, but auto-save must reflect the
 *     textarea exactly so refreshes don't resurrect deleted text.
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GlobalContext } from '@/app/providers/globalState';
import { usePlaygroundApi } from '@/features/playground/hooks/usePlaygroundApi';
import { buildTeacherModelDisplay } from '@/features/model-setup/utils/teacherModelUtils';
import {
    hasConfiguredPrompt,
    siblingsNeedingPromptPropagation,
} from '@/features/tasks/utils/promptPropagation';

const DEFAULT_PROVIDER = 'openai';
export const AUTOSAVE_DEBOUNCE_MS = 700;

const normalizePrompt = (raw) => {
    const value = (raw ?? '').toString();
    if (!value) return '';
    return value.includes('{{example}}') ? value : `${value} {{example}}`;
};

/** Trimmed string or null — the persistence shape of the optional teacher fields. */
const normalizeOptionalField = (raw) => (raw ?? '').toString().trim() || null;

/**
 * @param {Object} args
 * @param {Object} args.usecase Current LLMUsecaseInfo row being edited.
 * @param {string} args.prompt Current prompt value (controlled by caller).
 * @param {string} [args.systemPrompt] Teacher system prompt (`teacher_system_prompt`).
 * @param {string} [args.teacherSchema] Teacher output JSON schema (`teacher_schema`).
 * @param {{provider: string, model: string|null}} args.teacherConfig Current teacher selection.
 * @param {Array<Object>} [args.bedrockCatalog] Bedrock catalog for resolving teacher_display.
 * @param {boolean} [args.enabled=true] Gate auto-save (e.g. modal closed → false).
 * @param {boolean} [args.propagateToSiblings=true] Deprecated alias kept for
 *   backward compatibility. ``false`` maps to ``propagationMode: 'none'``.
 *   When both are supplied, ``propagationMode`` wins.
 * @param {('auto'|'all-siblings'|'none')} [args.propagationMode='auto']
 *   - ``'auto'``: current behaviour — first-time sessions fan out to
 *     empty sibling subtasks; later edits don't touch siblings.
 *   - ``'all-siblings'``: every save propagates to every sibling, even
 *     ones that are already configured. Used when the modal is opened
 *     from the header-level "Task Prompt" button on a uniform composed
 *     task, where the user is editing the shared config across all
 *     subtasks at once.
 *   - ``'none'``: never touch siblings.
 * @param {string} [args.role] Auth role; "Viewer" disables saving.
 * @param {(payload: Object) => void} [args.onSaved] Optional callback after each
 *   successful save so callers can sync their own local state (e.g. ``localUsecase``).
 *
 * @returns {{
 *   autosaving: boolean,
 *   autosaveError: string|null,
 *   isDirty: boolean,
 *   canSave: boolean,
 *   siblingsToPropagate: Array<Object>,
 *   flushSave: () => Promise<{savedSomething: boolean, propagatedIds: string[]}>,
 *   saveTeacherSelectionNow: (teacherConfig: {provider: string, model: string|null}) => Promise<{savedSomething: boolean, propagatedIds: string[]}>,
 *   clearAutosaveError: () => void,
 * }}
 */
export const useTeacherPromptAutoSave = ({
    usecase,
    prompt,
    systemPrompt,
    teacherSchema,
    teacherConfig,
    bedrockCatalog,
    enabled = true,
    propagateToSiblings = true,
    propagationMode,
    role,
    onSaved,
}) => {
    const api = usePlaygroundApi();
    const { currentSubGroup, updateUsecase } = useContext(GlobalContext);

    // Resolve mode once per render so both the memo and persistSave see the
    // same effective value. ``propagationMode`` takes precedence when given;
    // otherwise we fall back to the legacy boolean (``false`` → ``'none'``).
    const effectivePropagationMode =
        propagationMode || (propagateToSiblings === false ? 'none' : 'auto');

    const [autosaving, setAutosaving] = useState(false);
    const [autosaveError, setAutosaveError] = useState(null);

    const lastSavedRef = useRef({
        prompt: '',
        teacher_provider: DEFAULT_PROVIDER,
        teacher_model: null,
        teacher_system_prompt: null,
        teacher_schema: null,
    });
    const saveTimerRef = useRef(null);
    const wasFirstTimeOpenRef = useRef(false);
    const propagatedSiblingsRef = useRef(new Set());
    const persistInFlightRef = useRef(false);

    const isViewer = role === 'Viewer';

    // Reset session whenever the active usecase changes or the consumer
    // toggles ``enabled`` (e.g. modal opening). Captures the "first-time"
    // mode at the start of the session so subsequent debounced saves
    // share the same propagation gate, even after the prompt becomes
    // non-empty mid-session.
    useEffect(() => {
        if (!enabled) {
            // The consumer just turned auto-save off (modal closed,
            // drawer closed without unmount, viewer mode). If a debounce
            // was queued we'd otherwise discard it here and silently
            // lose the user's last keystroke — flush it instead so
            // closing fast still persists. Safe to fire even though the
            // debounce effect's cleanup ran first and clearTimeout'd
            // the pending callback: we read the freshest input via
            // ``latestRef`` and ``persistInFlightRef`` already guards
            // against an in-flight save racing this one.
            if (saveTimerRef.current != null) {
                clearTimeout(saveTimerRef.current);
                saveTimerRef.current = null;
                persistSave({ allowEmptyPrompt: true }).catch(() => {
                    // Auto-save lost the host — there's no UI left to
                    // surface the error on. Manual retries flow through
                    // ``flushSave`` / ``saveTeacherSelectionNow``.
                });
            }
            setAutosaving(false);
            return;
        }
        const initialPrompt = usecase?.prompt || '';
        const initialProvider = usecase?.teacher_provider || DEFAULT_PROVIDER;
        const initialModel = usecase?.teacher_model || null;

        lastSavedRef.current = {
            prompt: initialPrompt,
            teacher_provider: initialProvider,
            teacher_model: initialModel,
            teacher_system_prompt: normalizeOptionalField(usecase?.teacher_system_prompt),
            teacher_schema: normalizeOptionalField(usecase?.teacher_schema),
        };
        wasFirstTimeOpenRef.current = !hasConfiguredPrompt(usecase);
        propagatedSiblingsRef.current = new Set();
        setAutosaveError(null);
        setAutosaving(false);
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, usecase?._id]);

    const trimmedPrompt = (prompt ?? '').trim();
    const canSave = trimmedPrompt.length > 0;

    const isDirty =
        prompt !== lastSavedRef.current.prompt ||
        (teacherConfig?.provider || DEFAULT_PROVIDER) !==
            lastSavedRef.current.teacher_provider ||
        (teacherConfig?.model || null) !== lastSavedRef.current.teacher_model ||
        normalizeOptionalField(systemPrompt) !==
            lastSavedRef.current.teacher_system_prompt ||
        normalizeOptionalField(teacherSchema) !==
            lastSavedRef.current.teacher_schema;

    const siblingsToPropagate = useMemo(
        () => {
            if (effectivePropagationMode === 'none') return [];
            return siblingsNeedingPromptPropagation({
                currentUsecaseId: usecase?._id,
                subGroupUsecases: currentSubGroup?.usecases,
                isFirstTimeSession: wasFirstTimeOpenRef.current,
                alreadyPropagatedIds: propagatedSiblingsRef.current,
                forceAllSiblings: effectivePropagationMode === 'all-siblings',
            });
        },
        // ``wasFirstTimeOpenRef`` and ``propagatedSiblingsRef`` are refs;
        // they don't trigger renders by themselves, but every prompt /
        // teacher edit recomputes this memo and re-reads them, which is
        // when their values matter for any consumer banner.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentSubGroup, usecase?._id, prompt, teacherConfig?.provider, teacherConfig?.model, effectivePropagationMode]
    );

    // Latest-ref pattern: every render writes the freshest closure values
    // into ``latestRef`` so the debounced save reads the current state
    // without us having to put unstable references (``api``,
    // ``bedrockCatalog``, ``onSaved``) in the auto-save effect's deps.
    // Without this, an unrelated parent re-render that returned a new
    // ``onSaved`` lambda would tear down the pending debounce timer and
    // the save would never fire — which is exactly what the user observed
    // on the Model and Prompt Setup page.
    const latestRef = useRef({
        api,
        bedrockCatalog,
        currentSubGroup,
        onSaved,
        prompt,
        propagationMode: effectivePropagationMode,
        systemPrompt,
        teacherConfig,
        teacherSchema,
        updateUsecase,
        usecase,
    });
    latestRef.current = {
        api,
        bedrockCatalog,
        currentSubGroup,
        onSaved,
        prompt,
        propagationMode: effectivePropagationMode,
        systemPrompt,
        teacherConfig,
        teacherSchema,
        updateUsecase,
        usecase,
    };

    const saveOne = useCallback(
        (params) =>
            new Promise((resolve, reject) => {
                try {
                    latestRef.current.api.saveTeacherAndPrompt(
                        params,
                        (result) => {
                            if (!result?.success) {
                                reject(
                                    new Error(
                                        result?.message ||
                                            'Failed to save model & prompt'
                                    )
                                );
                                return;
                            }
                            resolve(result);
                        }
                    );
                } catch (err) {
                    reject(err);
                }
            }),
        []
    );

    // Persist the latest state to the backend. Always saves the active
    // subtask, then fans out to any siblings dictated by the composite-
    // task rule. Updates ``lastSavedRef`` and the global usecase store so
    // downstream consumers see fresh values without waiting for a
    // refetch. All inputs are read off ``latestRef`` so this callback is
    // stable across renders.
    const persistSave = useCallback(async ({
        allowEmptyPrompt = false,
        teacherConfigOverride = null,
    } = {}) => {
        const {
            bedrockCatalog: latestBedrock,
            currentSubGroup: latestSubGroup,
            onSaved: latestOnSaved,
            prompt: latestPrompt,
            propagationMode: latestPropagationMode,
            systemPrompt: latestSystemPrompt,
            teacherConfig: latestTeacherConfig,
            teacherSchema: latestTeacherSchema,
            updateUsecase: latestUpdateUsecase,
            usecase: latestUsecase,
        } = latestRef.current;

        if (!latestUsecase?._id || !latestUsecase?.client_id) {
            throw new Error('Task not loaded yet');
        }
        if (latestPrompt == null && !allowEmptyPrompt) {
            // Caller (page Next button) is responsible for surfacing a
            // user-facing validation message; the hook just refuses to
            // save before the prompt input has hydrated.
            throw new Error('Please enter a prompt.');
        }
        if (persistInFlightRef.current) {
            // A flush+autosave race could trigger two concurrent saves.
            // Treat the second as a no-op so callers don't double-fire.
            return { savedSomething: false, propagatedIds: [] };
        }
        persistInFlightRef.current = true;

        try {
            const promptHasValue = Boolean((latestPrompt ?? '').trim());
            const finalPrompt = promptHasValue
                ? normalizePrompt(latestPrompt)
                : '';
            const effectiveTeacherConfig = teacherConfigOverride || latestTeacherConfig;
            const provider = effectiveTeacherConfig?.provider || DEFAULT_PROVIDER;
            const teacherModelId = effectiveTeacherConfig?.model;
            const teacherDisplay = buildTeacherModelDisplay({
                provider,
                teacherModelId,
                bedrockCatalog: latestBedrock || [],
                fallbackDisplay: latestUsecase?.teacher_model_display || null,
            });

            const teacherSystemPrompt = normalizeOptionalField(latestSystemPrompt);
            const teacherSchemaValue = normalizeOptionalField(latestTeacherSchema);

            const sharedPayload = {
                teacher: teacherModelId || '',
                prompt: finalPrompt,
                provider,
                teacher_display: teacherDisplay,
                // The backend treats ``null`` as "leave unchanged" (for older
                // SDK/MCP callers) and an empty string as an explicit clear,
                // so always send strings here.
                teacher_system_prompt: teacherSystemPrompt || '',
                teacher_schema: teacherSchemaValue || '',
            };

            await saveOne({
                client_id: latestUsecase.client_id,
                llm_usecase_id: latestUsecase._id,
                ...sharedPayload,
            });

            const propagationTargets = latestPropagationMode === 'none'
                ? []
                : siblingsNeedingPromptPropagation({
                    currentUsecaseId: latestUsecase._id,
                    subGroupUsecases: latestSubGroup?.usecases,
                    isFirstTimeSession: wasFirstTimeOpenRef.current,
                    alreadyPropagatedIds: propagatedSiblingsRef.current,
                    forceAllSiblings: latestPropagationMode === 'all-siblings',
                });

            for (const sibling of propagationTargets) {
                if (!sibling?._id || !sibling?.client_id) continue;
                await saveOne({
                    client_id: sibling.client_id,
                    llm_usecase_id: sibling._id,
                    ...sharedPayload,
                });
                propagatedSiblingsRef.current.add(sibling._id);
                if (typeof latestUpdateUsecase === 'function') {
                    latestUpdateUsecase(sibling._id, {
                        prompt: finalPrompt,
                        teacher_model: teacherModelId || '',
                        teacher_provider: provider,
                        teacher_model_display: teacherDisplay,
                        teacher_system_prompt: teacherSystemPrompt,
                        teacher_schema: teacherSchemaValue,
                    });
                }
            }

            // Sync the active subtask's local copy too so consumers that
            // read directly from ``GlobalContext`` (e.g. the modal's
            // parent) see the freshly saved values immediately.
            if (typeof latestUpdateUsecase === 'function' && latestUsecase?._id) {
                latestUpdateUsecase(latestUsecase._id, {
                    prompt: finalPrompt,
                    teacher_model: teacherModelId || '',
                    teacher_provider: provider,
                    teacher_model_display: teacherDisplay,
                    teacher_system_prompt: teacherSystemPrompt,
                    teacher_schema: teacherSchemaValue,
                });
            }

            lastSavedRef.current = {
                prompt: promptHasValue ? latestPrompt : finalPrompt,
                teacher_provider: provider,
                teacher_model: teacherModelId || null,
                teacher_system_prompt: teacherSystemPrompt,
                teacher_schema: teacherSchemaValue,
            };

            const propagatedIds = Array.from(propagatedSiblingsRef.current);
            if (typeof latestOnSaved === 'function') {
                latestOnSaved({
                    llm_usecase_id: latestUsecase._id,
                    prompt: finalPrompt,
                    raw_prompt: promptHasValue ? latestPrompt : finalPrompt,
                    teacher_model: teacherModelId,
                    teacher_provider: provider,
                    teacher_model_display: teacherDisplay,
                    teacher_system_prompt: teacherSystemPrompt,
                    teacher_schema: teacherSchemaValue,
                    propagated_to_usecase_ids: propagatedIds,
                });
            }

            return { savedSomething: true, propagatedIds };
        } finally {
            persistInFlightRef.current = false;
        }
    }, [saveOne]);

    // Debounced auto-save while the user is editing. Deps cover every
    // user-driven input that should reset the debounce window (prompt
    // text, teacher provider/model) plus the gating booleans, but
    // explicitly exclude unstable references like ``api`` /
    // ``bedrockCatalog`` / consumer ``onSaved`` lambdas. Those are read
    // off ``latestRef`` inside ``persistSave`` so an unrelated parent
    // re-render that returned a new lambda doesn't restart this timer
    // and starve the save from ever firing.
    useEffect(() => {
        if (!enabled) return;
        if (isViewer) return;
        if (!usecase?._id || !usecase?.client_id) return;
        if (!isDirty) return;

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

        let cancelled = false;
        saveTimerRef.current = setTimeout(async () => {
            saveTimerRef.current = null;
            try {
                setAutosaving(true);
                setAutosaveError(null);
                await persistSave({ allowEmptyPrompt: true });
            } catch (e) {
                if (cancelled) return;
                setAutosaveError(e?.message || 'Failed to save model & prompt');
            } finally {
                if (!cancelled) setAutosaving(false);
            }
        }, AUTOSAVE_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
        };
    }, [
        // User-driven input that should reset the debounce window:
        prompt,
        systemPrompt,
        teacherSchema,
        teacherConfig?.provider,
        teacherConfig?.model,
        // Gating booleans / identifiers:
        enabled,
        isViewer,
        usecase?._id,
        usecase?.client_id,
        canSave,
        isDirty,
        // Stable callback (only depends on ``saveOne`` which has empty deps):
        persistSave,
    ]);

    // Flush a pending debounce on unmount (tab switch, drawer close). React
    // runs effect cleanups in reverse declaration order, so this effect is
    // declared after the debounce effect and its cleanup runs first: it still
    // sees ``saveTimerRef`` before the debounce cleanup clearTimeouts it.
    // persistSave reads the latest keystroke via ``latestRef``.
    useEffect(() => {
        return () => {
            if (saveTimerRef.current == null) return;
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
            persistSave({ allowEmptyPrompt: true }).catch(() => {
                // No UI left to surface the error on after unmount; manual
                // retries flow through Done / Next via ``flushSave``.
            });
        };
    }, [persistSave]);

    const flushSave = useCallback(async () => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        // Recompute dirtiness from the freshest values, not the
        // closed-over render snapshot, so a Done/Next click that races
        // the typing keystroke still sees the latest user input.
        const {
            prompt: latestPrompt,
            systemPrompt: latestSystemPrompt,
            teacherConfig: latestTeacherConfig,
            teacherSchema: latestTeacherSchema,
        } = latestRef.current;
        const dirtyNow =
            latestPrompt !== lastSavedRef.current.prompt ||
            (latestTeacherConfig?.provider || DEFAULT_PROVIDER) !==
                lastSavedRef.current.teacher_provider ||
            (latestTeacherConfig?.model || null) !==
                lastSavedRef.current.teacher_model ||
            normalizeOptionalField(latestSystemPrompt) !==
                lastSavedRef.current.teacher_system_prompt ||
            normalizeOptionalField(latestTeacherSchema) !==
                lastSavedRef.current.teacher_schema;
        if (!dirtyNow) {
            return { savedSomething: false, propagatedIds: [] };
        }
        try {
            setAutosaving(true);
            setAutosaveError(null);
            const result = await persistSave();
            return result;
        } catch (e) {
            setAutosaveError(e?.message || 'Failed to save model & prompt');
            throw e;
        } finally {
            setAutosaving(false);
        }
    }, [persistSave]);

    const saveTeacherSelectionNow = useCallback(async (nextTeacherConfig) => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        if (!enabled || isViewer) {
            return { savedSomething: false, propagatedIds: [] };
        }
        try {
            setAutosaving(true);
            setAutosaveError(null);
            return await persistSave({
                allowEmptyPrompt: true,
                teacherConfigOverride: nextTeacherConfig,
            });
        } catch (e) {
            setAutosaveError(e?.message || 'Failed to save model & prompt');
            throw e;
        } finally {
            setAutosaving(false);
        }
    }, [enabled, isViewer, persistSave]);

    const clearAutosaveError = useCallback(() => setAutosaveError(null), []);

    return {
        autosaving,
        autosaveError,
        isDirty,
        canSave,
        siblingsToPropagate,
        flushSave,
        saveTeacherSelectionNow,
        clearAutosaveError,
    };
};
